import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function ArticlePage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setMissing(false);
    axios
      .get(`${API}/insights/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setMissing(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    document.title = `${post.title} | BYOT Insights`;
    meta?.setAttribute("content", post.excerpt);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [post]);

  return (
    <main data-testid="article-page" className="grain relative mx-auto min-h-screen max-w-3xl px-5 pb-24 pt-36 sm:px-8 md:pt-44">
      {missing && (
        <div data-testid="article-not-found" className="text-center">
          <h1 className="font-serif text-4xl text-[#F7F5F0]">Article not found.</h1>
          <a href="/" data-testid="article-home-link" className="mt-6 inline-flex items-center gap-2 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <ArrowLeft size={14} /> Return home
          </a>
        </div>
      )}

      {!post && !missing && (
        <div data-testid="article-loading" className="flex flex-col gap-5">
          <div className="h-4 w-40 animate-pulse bg-[#16243E]" />
          <div className="h-12 w-full animate-pulse bg-[#16243E]" />
          <div className="h-12 w-2/3 animate-pulse bg-[#16243E]" />
          <div className="mt-8 h-4 w-full animate-pulse bg-[#0F1A2E]" />
          <div className="h-4 w-full animate-pulse bg-[#0F1A2E]" />
          <div className="h-4 w-5/6 animate-pulse bg-[#0F1A2E]" />
        </div>
      )}

      {post && (
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <a
            href="/#insights"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#94A3B8] transition-colors hover:text-[#D4AF37]"
          >
            <ArrowLeft size={14} /> Insights Journal
          </a>

          <h1 data-testid="article-title" className="mt-8 font-serif text-3xl leading-tight tracking-tight text-[#F7F5F0] sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>

          <div data-testid="article-byline" className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-[rgba(212,175,55,0.2)] py-4 font-mono-alt text-[10px] uppercase tracking-[0.22em] text-[#94A3B8]">
            <span className="text-[#D4AF37]">{post.author}</span>
            <span>{formatDate(post.date)}</span>
            <span>{post.read_time}</span>
          </div>

          <div data-testid="article-body" className="mt-10">
            {post.content.split("\n\n").map((para, i) => (
              <p
                key={i}
                className={`mb-7 leading-[1.85] ${
                  i === 0
                    ? "font-serif text-xl text-[#E2E8F0] sm:text-2xl"
                    : "text-base text-[#94A3B8] sm:text-lg"
                }`}
              >
                {para}
              </p>
            ))}
          </div>

          <div className="mt-14 border border-[rgba(212,175,55,0.25)] bg-[#0F1A2E]/80 p-8">
            <p className="font-mono-alt text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
              Continue the conversation
            </p>
            <p className="mt-4 font-serif text-2xl text-[#F7F5F0]">
              Facing a mandate this touches? The strategy desk responds within one business day.
            </p>
            <a
              href="/#contact"
              data-testid="article-cta-contact"
              className="group mt-6 inline-flex items-center gap-3 bg-[#D4AF37] px-7 py-4 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#070E1B] transition-colors duration-300 hover:bg-[#F3E5AB]"
            >
              Request Consultation
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </motion.article>
      )}
    </main>
  );
}
